interface ServerData {
  amenities: any[];
  bnbUrl: string;
  /**
   * Server-side flag flipped by the "Unsync Airbnb Pricing" tool. `false` means Airbnb price
   * sync has been stopped for this property — the Calendar Pricing page uses it to hide the
   * saved listing link. Omitted / undefined for properties predating the flag == still enabled.
   */
  airbnbPriceSyncEnabled?: boolean | null;
  property3dUrl: string;
  uspsAndView?: string;
  houseRules?: string;
  elderlyFriendly?: string | boolean | null;
  childFriendly?: string | boolean | null;
  petFriendly?: string | boolean | null;
  securityGuard24x7?: string | boolean | null;
  propertyStructureType?: string | null;
  poolLength?: string | null;
  poolBreadth?: string | null;
  poolDepth?: string | null;
  parkingDescription?: string | null;
  flexibleCheckinType?: string | null;
  propertyMetadata?: {
    uspsAndView?: string;
    houseRules?: string;
    elderlyFriendly?: string | boolean | null;
    childFriendly?: string | boolean | null;
    petFriendly?: string | boolean | null;
    securityGuard24x7?: string | boolean | null;
    propertyStructureType?: string | null;
    poolLength?: string | null;
    poolBreadth?: string | null;
    poolDepth?: string | null;
    parkingDescription?: string | null;
    flexibleCheckinType?: string | null;
  } | null;
  description: string;
  details: any[];
  location: any;
  onboardingStage: number;
  propertyId: string;
  propertyOffering: {
    id: number;
    name: string;
    description?: string;
  };
  propertyTypeId: number;
  propertyTypeOther?: string | null;
  title: string;
  /** Display-only brand-free copy (an admin-imported listing that is not Host Pro); title / description stay the original. */
  displayTitle?: string | null;
  displayDescription?: string | null;
}

interface PropertyTypeListMap {
  [key: number]: {
    name: string;
  };
}

interface PropertyOfferingsMap {
  [key: number]: string;
}

interface TypeMapItem {
  id: number;
  name: string;
}

const POOL_NAMES = ['pvt pool', 'private pool', 'shared pool', 'pool'];
const PARKING_NAMES = ['pvt car park', 'private parking', 'shared parking', 'parking'];
const CHECKIN_NAMES = ['flexi checkin/out', 'flexible checkin', 'flexi check-in'];
const CHECKIN_VALUES = ['self', 'assisted', 'front_desk', 'digital'];

const isPoolAmenity = (name: string) =>
  POOL_NAMES.some((p) => name.toLowerCase().includes(p));
const isParkingAmenity = (name: string) =>
  PARKING_NAMES.some((p) => name.toLowerCase().includes(p));
const isCheckinAmenity = (name: string) =>
  CHECKIN_NAMES.some((p) => name.toLowerCase().includes(p));

function parsePoolDimFromAmenities(amenities: any[] | null | undefined, dim: 'length' | 'width' | 'depth'): string {
  if (!amenities) return '';
  for (const a of amenities) {
    if (!isPoolAmenity(String(a.name || ''))) continue;
    const desc = String(a.description || '');
    const match = desc.match(new RegExp(`${dim}:\\s*([^,]+)`, 'i'));
    if (match) return match[1].trim();
  }
  return '';
}

function parseParkingDescFromAmenities(amenities: any[] | null | undefined): string {
  if (!amenities) return '';
  for (const a of amenities) {
    if (!isParkingAmenity(String(a.name || ''))) continue;
    const desc = String(a.description || '');
    if (desc && desc !== a.name) return desc;
  }
  return '';
}

function parseCheckinTypeFromAmenities(amenities: any[] | null | undefined): string {
  if (!amenities) return '';
  for (const a of amenities) {
    if (!isCheckinAmenity(String(a.name || ''))) continue;
    const desc = String(a.description || '').toLowerCase().replace(/[-\s]+/g, '_');
    if (CHECKIN_VALUES.includes(desc)) return desc;
  }
  return '';
}

export const transformPropertyDataFromServer = (
  serverData: ServerData,
  propertyTypeListMap: PropertyTypeListMap,
  propertyOfferingsMap: PropertyOfferingsMap,
  // cityMap,
  typeMap?: TypeMapItem[],
): any => {
  const {
    amenities,
    bnbUrl,
    airbnbPriceSyncEnabled,
    property3dUrl,
    uspsAndView,
    houseRules,
    elderlyFriendly,
    childFriendly,
    petFriendly,
    securityGuard24x7,
    propertyStructureType,
    poolLength,
    poolBreadth,
    poolDepth,
    parkingDescription,
    flexibleCheckinType,
    propertyMetadata,
    description,
    details,
    location,
    onboardingStage,
    propertyId,
    propertyOffering,
    propertyTypeId,
    propertyTypeOther,
    title,
    displayTitle,
    displayDescription,
  } = serverData;

  const offerings: Record<number, any> = {};
  const offeringsMap: Record<number, any> = {};
  details.forEach((detail) => {
    offerings[detail.propertyDetailsId] = detail.detailsValue;
    offeringsMap[detail.propertyDetailsId] = { ...detail };
  });

  let subType: { id?: number; name?: string } | null = null;
  if (propertyOffering.description) {
    const subTypeId = typeMap?.filter(
      (item) => item.name === propertyOffering.description,
    );

    subType = {
      id: subTypeId?.[0]?.id,
      name: subTypeId?.[0]?.name,
    };
  }

  const normalizeYesNo = (v: unknown): '' | 'yes' | 'no' => {
    if (v === true || v === 'true' || v === 'yes' || v === 'YES') return 'yes';
    if (v === false || v === 'false' || v === 'no' || v === 'NO') return 'no';
    const s = String(v || '')
      .trim()
      .toLowerCase();
    if (s === 'y' || s === 'yes') return 'yes';
    if (s === 'n' || s === 'no') return 'no';
    return '';
  };

  const normalizeStructureType = (v: unknown): '' | 'standalone' | 'gated_complex' => {
    const s = String(v || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
    if (!s) return '';
    if (s.includes('gated')) return 'gated_complex';
    if (s.includes('standalone')) return 'standalone';
    return '';
  };

  const normalizeFlexibleCheckinType = (
    v: unknown,
  ): '' | 'self' | 'assisted' | 'front_desk' | 'digital' => {
    const s = String(v || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/-/g, '_');
    if (s === 'self') return 'self';
    if (s === 'assisted') return 'assisted';
    if (s === 'front_desk' || s === 'frontdesk' || s === 'staffed' || s === 'reception') {
      return 'front_desk';
    }
    if (s === 'digital' || s === 'digital_keyless' || s === 'keyless') return 'digital';
    return '';
  };

  const dataToSet = {
    propertyId,
    detail: {
      title,
      description,
      ...(displayTitle ? { displayTitle } : {}),
      ...(displayDescription ? { displayDescription } : {}),
      bnbUrl,
      // Only forward an explicit boolean; leave it undefined otherwise so the "sync enabled
      // unless told otherwise" default in the consumers holds.
      ...(typeof airbnbPriceSyncEnabled === 'boolean'
        ? { airbnbPriceSyncEnabled }
        : {}),
      property3dUrl,
      uspsAndView: propertyMetadata?.uspsAndView || uspsAndView || '',
      houseRules: propertyMetadata?.houseRules || houseRules || '',
      elderlyFriendly: normalizeYesNo(
        propertyMetadata?.elderlyFriendly ?? elderlyFriendly,
      ),
      childFriendly: normalizeYesNo(
        propertyMetadata?.childFriendly ?? childFriendly,
      ),
      petFriendly: normalizeYesNo(propertyMetadata?.petFriendly ?? petFriendly),
      securityGuard24x7: normalizeYesNo(
        propertyMetadata?.securityGuard24x7 ?? securityGuard24x7,
      ),
      propertyStructureType: normalizeStructureType(
        propertyMetadata?.propertyStructureType ?? propertyStructureType,
      ),
      poolLength:
        propertyMetadata?.poolLength != null
          ? String(propertyMetadata.poolLength)
          : poolLength != null
            ? String(poolLength)
            : parsePoolDimFromAmenities(amenities, 'length'),
      poolBreadth:
        propertyMetadata?.poolBreadth != null
          ? String(propertyMetadata.poolBreadth)
          : poolBreadth != null
            ? String(poolBreadth)
            : parsePoolDimFromAmenities(amenities, 'width'),
      poolDepth:
        propertyMetadata?.poolDepth != null
          ? String(propertyMetadata.poolDepth)
          : poolDepth != null
            ? String(poolDepth)
            : parsePoolDimFromAmenities(amenities, 'depth'),
      parkingDescription:
        propertyMetadata?.parkingDescription != null
          ? String(propertyMetadata.parkingDescription)
          : parkingDescription != null
            ? String(parkingDescription)
            : parseParkingDescFromAmenities(amenities),
      flexibleCheckinType: normalizeFlexibleCheckinType(
        propertyMetadata?.flexibleCheckinType ?? flexibleCheckinType ?? parseCheckinTypeFromAmenities(amenities),
      ),
    },
    offeringsMap: offeringsMap,
    offerings: { ...offerings },
    location: {
      ...location,
      country: 'India (IN)',
      district: location.locality,
      coordinates: { lat: location.latitude, lng: location.longitude },
      zoneId: location.zoneId,
      city: location?.city,
      state: location.state,
    },
    amenitiesObject: [...amenities],
    amenities: amenities.map((amenityItem) => amenityItem.name),
    onboardingStage,
    type: { id: propertyOffering.id, name: propertyOffering.name },
    subType: { ...subType }, //TODO
    place: {
      id: propertyTypeId,
      name: propertyTypeListMap[propertyTypeId].name,
      other: propertyTypeOther || '',
    },
  };

  return dataToSet;
};

interface PricingServerData {
  propertyPrices: any[];
  discounts: any[];
  additionalCharges: any[];
  defaultPrice: number;
  pricingRangeMetadata?: string;
  propertyCalendarPrices?: any[];
  maxGuestAllowed: number;
  weekendDefaultPrice?: number | null;
  weekdayMinNights?: number | null;
  weekendMinNights?: number | null;
  weekendDays?: string | null;
  securityDeposit?: number | null;
}

export const transformPropertyPricing = (
  serverData: PricingServerData,
): any => {
  const {
    propertyPrices,
    discounts,
    additionalCharges,
    defaultPrice,
    pricingRangeMetadata,
    propertyCalendarPrices,
    maxGuestAllowed,
  } = serverData;

  const hasCalendarPrices =
    Array.isArray(propertyCalendarPrices) && propertyCalendarPrices.length > 0;
  const hasLegacyPrices =
    Array.isArray(propertyPrices) && propertyPrices.length >= 3;
  // Airbnb-imported listings come back with per-date calendar prices but no explicit
  // default price. Derive the weekday default from them so the field isn't left blank.
  const deriveWeekdayPriceFromCalendar = (): number => {
    if (!hasCalendarPrices) return 0;
    const counts = new Map<number, number>();
    const all = new Map<number, number>();
    const todayStr = new Date().toISOString().slice(0, 10);
    for (const item of propertyCalendarPrices as any[]) {
      const price = Number(item?.pricePerNight);
      const date = String(item?.priceDate || '');
      if (!Number.isFinite(price) || price <= 0 || !date) continue;
      all.set(price, (all.get(price) || 0) + 1);
      const dow = new Date(`${date}T00:00:00`).getDay(); // 1..4 = Mon..Thu
      if (date >= todayStr && dow >= 1 && dow <= 4) {
        counts.set(price, (counts.get(price) || 0) + 1);
      }
    }
    const pool = counts.size > 0 ? counts : all;
    let best = 0;
    let bestCount = 0;
    pool.forEach((count, price) => {
      if (count > bestCount) {
        best = price;
        bestCount = count;
      }
    });
    return best;
  };

  const resolvedDefaultPrice =
    typeof defaultPrice === 'number' && defaultPrice > 0
      ? defaultPrice
      : hasLegacyPrices
        ? propertyPrices[0]?.pricePerNight || 0
        : deriveWeekdayPriceFromCalendar();

  const pricingDetails = {
    weekday: {
      priceId: !hasCalendarPrices && hasLegacyPrices ? propertyPrices[0].priceId : 0,
      pricePerNight: !hasCalendarPrices && hasLegacyPrices
        ? propertyPrices[0].pricePerNight
        : 0,
      numberOfGuests: !hasCalendarPrices && hasLegacyPrices
        ? propertyPrices[0].maxGuestAllowed
        : 0,
    },
    weekend: {
      priceId: !hasCalendarPrices && hasLegacyPrices ? propertyPrices[1].priceId : 0,
      pricePerNight: !hasCalendarPrices && hasLegacyPrices
        ? propertyPrices[1].pricePerNight
        : 0,
      numberOfGuests: !hasCalendarPrices && hasLegacyPrices
        ? propertyPrices[1].maxGuestAllowed
        : 0,
    },
    maxGuestAllowed: {
      numberOfGuests: maxGuestAllowed,
    },
    propertyCalendarPrices: hasCalendarPrices
      ? propertyCalendarPrices.reduce(
          (acc, item) => {
            acc[item.priceDate] = item.pricePerNight;
            return acc;
          },
          {} as Record<string, number>,
        )
      : null,
    additionalCharges: additionalCharges.map((additionalCharge) => {
      return {
        ...additionalCharge,
        amount: additionalCharge.pricePerNight,
        paymentType: additionalCharge.chargeType,
      };
    }),
    friday: {
      priceId: !hasCalendarPrices && hasLegacyPrices ? propertyPrices[2].priceId : 0,
      isSelected:
        !hasCalendarPrices &&
        hasLegacyPrices &&
        propertyPrices[0].pricePerNight === propertyPrices[2].pricePerNight
          ? 'weekday'
          : 'weekend',
    },

    discounts: {
      lessThan24Hours: discounts?.[0]?.discountPercentage || 0,
      between24HoursTo72Hours:
        discounts?.[1]?.discountPercentage || 0,
      equalToOrMoreThan72Hours:
        discounts?.[2]?.discountPercentage || 0,
      equalToOrMoreThan7Days:
        discounts?.[3]?.discountPercentage || 0,
    },
    discountStore:
      discounts?.length > 0
        ? {
            lessThan24Hours: discounts[0],
            between24HoursTo72Hours: discounts[1],
            equalToOrMoreThan72Hours: discounts[2],
            equalToOrMoreThan7Days: discounts[3],
          }
        : null,
    basePrice: pricingRangeMetadata ? JSON.parse(pricingRangeMetadata) : {},
    defaultPrice: resolvedDefaultPrice,
    weekendDefaultPrice: serverData.weekendDefaultPrice ?? undefined,
    weekdayMinNights: serverData.weekdayMinNights ?? undefined,
    weekendMinNights: serverData.weekendMinNights ?? undefined,
    weekendDays: serverData.weekendDays ?? undefined,
    securityDeposit: serverData.securityDeposit ?? undefined,
    //  cleaningFees: {
    //    lessThan3NightsBooking: {
    //      paymentType: 'one_time',
    //      amount: 0,
    //    },
    //    moreThan3NightsBooking: {
    //      paymentType: 'one_time',
    //      amount: 0,
    //    },
  };

  return pricingDetails;
};

export function convertDateFormat(dateString: string): string | null {
  console.log(dateString);
  if (dateString) {
    const [month, day, year] = dateString.split('-');
    return `${year}-${month}-${day}`;
  } else return null;
}

export function convertArrayFormat(data: string): number[] | null {
  if (data) {
    const updatedData = data.split(',').map(Number);
    return updatedData;
  } else return null;
}

export const formatCurrencyAmount = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
});

export const PRICE_BREAKDOWN_KEYS = {
  basic: 'Base Price',
  'Price Per Additional Guest': 'Price for Additional Guest',
  'Cleaning fee': 'Cleaning Fee',
  serviceFee: 'Service Fee',
  tax: 'GST',
  discount: 'Discount',
};

export function dateDifference(
  date1: string | Date,
  date2: string | Date,
): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}
